import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CreditCard, Calendar, DollarSign, CheckCircle, Edit, Trash2 } from "lucide-react";
import { AdvancedEditTransactionModal } from "@/components/transactions/AdvancedEditTransactionModal";
import { useCreditCardOperations } from "@/hooks/useCreditCardOperations";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { supabase } from "@/integrations/supabase/client";
import { formatarDataSQL, getInvoiceStatus, getNomeMes } from "@/lib/dateUtils";
import { toast } from "sonner";
import { Conta, FaturaCartao, Transaction, Category, CompraParcelada } from "@/types/database";
import { format } from 'date-fns';

interface InvoiceManagerProps {
  card: Conta;
  onBack: () => void;
  onSuccess: () => void;
  targetDate?: string;
  targetInvoiceId?: string;
}

interface TransactionWithCategory extends Transaction {
  categoria?: Category;
  compras_parceladas?: Array<CompraParcelada>;
}

export const InvoiceManager: React.FC<InvoiceManagerProps> = ({
  card,
  onBack,
  onSuccess,
  targetDate,
  targetInvoiceId,
}) => {
  const [invoices, setInvoices] = useState<FaturaCartao[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<FaturaCartao | null>(null);
  const [transactions, setTransactions] = useState<TransactionWithCategory[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<Conta[]>([]);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState<string>("");
  const [paymentDate, setPaymentDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<TransactionWithCategory | null>(null);

  const { confirmAndRemoveInstallment } = useCreditCardOperations();

  /**
   * Replicates the calcular_fatura_compra DB logic in JS.
   * Returns {mes, ano} (1-indexed month) indicating which invoice a transaction belongs to.
   */
  const calcularFaturaCompraLocal = useCallback((dataCompra: Date): { mes: number; ano: number } => {
    let mes = dataCompra.getMonth() + 1; // JS months are 0-indexed
    let ano = dataCompra.getFullYear();

    const ultimoDiaMes = (y: number, m: number) => new Date(y, m, 0).getDate();

    if (card.dia_fechamento != null) {
      const diaFechamentoAjustado = Math.min(card.dia_fechamento, ultimoDiaMes(ano, mes));
      const dataFechamento = new Date(ano, mes - 1, diaFechamentoAjustado);

      if (dataCompra > dataFechamento) {
        if (mes === 12) { mes = 1; ano += 1; }
        else { mes += 1; }
      }
    } else if (card.dia_vencimento != null) {
      const diaVencimentoAjustado = Math.min(card.dia_vencimento, ultimoDiaMes(ano, mes));
      const dataVencimento = new Date(ano, mes - 1, diaVencimentoAjustado);

      if (dataCompra > dataVencimento) {
        if (mes === 12) { mes = 1; ano += 1; }
        else { mes += 1; }
      }
    }

    return { mes, ano };
  }, [card.dia_fechamento, card.dia_vencimento]);

  const fetchInvoices = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("faturas_cartao")
        .select("*")
        .eq("cartao_id", card.id)
        .order("paga", { ascending: true })
        .order("data_vencimento", { ascending: true });

      if (error) {
        console.error("Erro ao buscar faturas:", error);
        return;
      }

      setInvoices(data || []);
      
      let invoiceToSelect = null;
      
      if (targetInvoiceId) {
        invoiceToSelect = data?.find((inv) => inv.id === targetInvoiceId) || null;
      }

      if (!invoiceToSelect && targetDate) {
        try {
          const parts = targetDate.split('T')[0].split('-');
          const tDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
          const { mes, ano } = calcularFaturaCompraLocal(tDate);
          
          invoiceToSelect = data?.find(inv => inv.mes_referencia === mes && inv.ano_referencia === ano);
        } catch (e) {
          console.error("Error parsing targetDate:", e);
        }
      }
      
      if (!invoiceToSelect) {
        // Select next unpaid invoice by default (earliest due date)
        invoiceToSelect = data?.find((inv) => !inv.paga) || null;
        
        if (!invoiceToSelect && data && data.length > 0) {
          // If all invoices are paid, select the most recent one
          invoiceToSelect = data[0];
        }
      }
      
      if (invoiceToSelect) {
        setSelectedInvoice(invoiceToSelect);
      }
    } catch (error) {
      console.error("Erro inesperado:", error);
    }
  }, [card.id, targetDate, targetInvoiceId, calcularFaturaCompraLocal]);

  const fetchTransactions = useCallback(async (invoice: FaturaCartao) => {
    if (!invoice) return;
    
    try {
      const { data: transactionsData, error } = await supabase
        .from('transacoes')
        .select(`
          *,
          categoria:categorias(*),
          compras_parceladas(*)
        `)
        .eq('conta_id', card.id)
        .eq('tipo', 'saida')
        .not('ignorar_transacao', 'is', true)
        .order('data', { ascending: false });
      
      if (error) {
        console.error('Error fetching transactions:', error);
        throw error;
      }
      
      // Filter transactions belonging to this invoice using local JS logic
      // (avoids N+1 RPC calls and silent per-row failures)
      const invoiceTransactions = (transactionsData || []).filter((transaction) => {
        try {
          // Parse date as local date to avoid UTC timezone shifting
          const parts = (transaction.data as string).split('T')[0].split('-');
          const transactionDate = new Date(
            parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])
          );
          const { mes, ano } = calcularFaturaCompraLocal(transactionDate);
          return mes === invoice.mes_referencia && ano === invoice.ano_referencia;
        } catch {
          return false;
        }
      });
      
      setTransactions(invoiceTransactions as unknown as TransactionWithCategory[]);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
  }, [card.id, calcularFaturaCompraLocal]);

  const markInvoiceTransactionsAsPaid = async (invoice: FaturaCartao, dataPagamento: string) => {
    try {
      let transactionIdsToUpdate = transactions
        .filter((transaction) => !transaction.ignorar_transacao && !transaction.foi_paga)
        .map((transaction) => transaction.id);

      if (transactionIdsToUpdate.length === 0) {
        const { data: transactionsData, error: fetchError } = await supabase
          .from('transacoes')
          .select('id, data')
          .eq('conta_id', card.id)
          .eq('tipo', 'saida')
          .or('ignorar_transacao.is.null,ignorar_transacao.eq.false')
          .or('foi_paga.is.null,foi_paga.eq.false');

        if (fetchError) {
          console.error('Error fetching transactions to mark as paid:', fetchError);
          return;
        }

        transactionIdsToUpdate = [];

        for (const transaction of transactionsData || []) {
          try {
            const parts = (transaction.data as string).split('T')[0].split('-');
            const transactionDate = new Date(
              parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])
            );
            const { mes, ano } = calcularFaturaCompraLocal(transactionDate);

            if (mes === invoice.mes_referencia && ano === invoice.ano_referencia) {
              transactionIdsToUpdate.push(transaction.id);
            }
          } catch (calcError) {
            console.error('Error calculating invoice for transaction:', calcError);
          }
        }
      }

      // Update all transactions of this invoice to paid
      if (transactionIdsToUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('transacoes')
          .update({ 
            foi_paga: true,
            data_pagamento: new Date(dataPagamento).toISOString()
          })
          .in('id', transactionIdsToUpdate);

        if (updateError) {
          console.error('Error marking transactions as paid:', updateError);
        } else {
          console.log(`Marked ${transactionIdsToUpdate.length} transactions as paid`);
          setTransactions((currentTransactions) =>
            currentTransactions.map((transaction) =>
              transactionIdsToUpdate.includes(transaction.id)
                ? {
                    ...transaction,
                    foi_paga: true,
                    data_pagamento: new Date(dataPagamento).toISOString()
                  }
                : transaction
            )
          );
        }
      }
    } catch (error) {
      console.error('Error in markInvoiceTransactionsAsPaid:', error);
    }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from("contas")
        .select("*")
        .neq("tipo", "cartao_credito")
        .eq("ativo", true)
        .order("nome");

      if (error) {
        console.error("Erro ao buscar contas:", error);
        return;
      }

      setPaymentAccounts((data || []) as Conta[]);
    } catch (error) {
      console.error("Erro inesperado:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transaction: TransactionWithCategory, deleteAll: boolean = false) => {
    try {
      // Verificar se é pai de alguma transação
      const { count: filhosCount } = await supabase
        .from("transacoes")
        .select("id", { count: "exact", head: true })
        .eq("transacao_pai_id", transaction.id);

      const hasChildren = (filhosCount || 0) > 0;

      const isInstallmentExpense = !!(transaction.compras_parceladas && transaction.compras_parceladas.length > 0) || 
                                    !!(transaction.total_parcelas && transaction.total_parcelas > 1) ||
                                    hasChildren;
      
      // Se for parcelada ou tiver pai (caso de repeticao/parcelada)
      if (isInstallmentExpense || transaction.transacao_pai_id) {
        if (deleteAll) {
          const originId = transaction.transacao_pai_id || transaction.id;
          const parcelas = transaction.total_parcelas || 
            (isInstallmentExpense && transaction.compras_parceladas?.[0]?.numero_parcelas) ||
            transaction.numero_total_repeticoes || 1;

          const success = await confirmAndRemoveInstallment(
            originId,
            transaction.descricao,
            transaction.valor,
            parcelas,
            true // Skip window.confirm since they already confirmed in AlertDialog
          );

          if (success) {
            if (editingTransaction?.id === transaction.id) {
              setEditingTransaction(null);
            }
            await fetchInvoices();
            if (selectedInvoice) fetchTransactions(selectedInvoice);
          }
        } else {
          // Excluir apenas a parcela atual fisicamente
          const { error: deleteError } = await supabase
            .from("transacoes")
            .delete()
            .eq("id", transaction.id);

          if (deleteError) throw deleteError;

          toast.success("Parcela excluída com sucesso!");
          if (editingTransaction?.id === transaction.id) {
            setEditingTransaction(null);
          }
          await fetchInvoices();
          if (selectedInvoice) fetchTransactions(selectedInvoice);
        }
        return;
      }

      // Deletar a normal
      const { error } = await supabase
        .from("transacoes")
        .delete()
        .eq("id", transaction.id);

      if (error) throw error;

      toast.success("Transação excluída com sucesso!");

      if (editingTransaction?.id === transaction.id) {
        setEditingTransaction(null);
      }

      await fetchInvoices();
      if (selectedInvoice) fetchTransactions(selectedInvoice);
    } catch (error) {
      console.error("Erro ao excluir transação:", error);
      toast.error("Erro ao excluir transação");
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchPaymentAccounts();
  }, [card.id, fetchInvoices]);

  useEffect(() => {
    if (selectedInvoice) {
      fetchTransactions(selectedInvoice);
    }
  }, [selectedInvoice, fetchTransactions]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return "";
      const onlyDateMatch = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
      let date;
      if (onlyDateMatch) {
        const [y, m, d] = dateString.split("-").map(Number);
        date = new Date(Date.UTC(y, m - 1, d));
      } else {
        date = new Date(dateString);
      }
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC"
      }).format(date);
    } catch {
      return "";
    }
  };

  const formatDateUTCFromParts = (year: number, month: number, day: number) => {
    try {
      const date = new Date(Date.UTC(year, month - 1, day));
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC'
      }).format(date);
    } catch {
      return '';
    }
  };

  const getMonthName = (month: number) => {
    return getNomeMes(month);
  };

  const handlePayInvoice = async () => {
    if (!selectedInvoice || !selectedPaymentAccount) {
      toast.error("Selecione uma conta para pagamento.");
      return;
    }

    if (!paymentDate) {
      toast.error("Informe a data do pagamento.");
      return;
    }

    setPayingInvoice(true);

    try {
      // Update invoice as paid
      const { error: invoiceError } = await supabase
        .from("faturas_cartao")
        .update({
          paga: true,
          data_pagamento: new Date(paymentDate).toISOString(),
          conta_pagamento_id: selectedPaymentAccount,
        })
        .eq("id", selectedInvoice.id);

      if (invoiceError) {
        console.error("Erro ao atualizar fatura:", invoiceError);
        toast.error("Erro ao marcar fatura como paga.");
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error: transferError } = await supabase
        .from("transferencias")
        .insert({
          conta_origem_id: selectedPaymentAccount,
          conta_destino_id: card.id,
          valor: selectedInvoice.valor_total,
          data: new Date(paymentDate).toISOString(),
          descricao: `Pagamento fatura ${card.nome} - ${getMonthName(selectedInvoice.mes_referencia)}/${selectedInvoice.ano_referencia}`,
          user_id: user.id,
        });

      if (transferError) {
        console.error("Erro ao criar transferência:", transferError);
        toast.warning("Fatura marcada como paga, mas houve erro ao registrar a transferência de pagamento.");
      }

      // Reset card's current invoice amount
      const { error: cardError } = await supabase
        .from("contas")
        .update({ fatura_atual: 0 })
        .eq("id", card.id);

      if (cardError) {
        console.error("Erro ao atualizar cartão:", cardError);
      }

      // Mark all invoice transactions as paid
      await markInvoiceTransactionsAsPaid(selectedInvoice, paymentDate);

      toast.success("Fatura paga com sucesso!");

      // Refresh data
      fetchInvoices();
      onSuccess();
    } catch (error) {
      console.error("Erro inesperado:", error);
      toast.error("Erro inesperado ao pagar fatura.");
    } finally {
      setPayingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="h-6 bg-muted rounded w-48 animate-pulse"></div>
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-muted rounded"></div>
                  <div className="h-3 bg-muted rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-base font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            {card.nome}
          </h2>
          <p className="text-muted-foreground">
            Limite: {formatCurrency(card.limite_credito || 0)} • 
            Vencimento dia {card.dia_vencimento}
          </p>
        </div>
      </div>

      {invoices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-base font-medium mb-2">Nenhuma fatura encontrada</h3>
            <p className="text-muted-foreground text-center">
              As faturas serão criadas automaticamente quando você fizer compras com este cartão.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value="current" className="w-full">
          <TabsList>
            <TabsTrigger value="current">Fatura Selecionada</TabsTrigger>
            <TabsTrigger value="history">Histórico</TabsTrigger>
          </TabsList>

          <TabsContent value="current" className="space-y-4">
            {/* Month/Year Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Selecionar Fatura
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Mês/Ano</Label>
                    <Select 
                      value={selectedInvoice?.id || ""} 
                      onValueChange={(value) => {
                        const invoice = invoices.find(inv => inv.id === value);
                        setSelectedInvoice(invoice || null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma fatura" />
                      </SelectTrigger>
                      <SelectContent>
                        {invoices.map((invoice) => {
                          const status = getInvoiceStatus(invoice, card);
                          const statusConfig = {
                            paga: { label: "Paga", variant: "default" as const, className: "bg-green-500 hover:bg-green-600" },
                            aberta: { label: "Aberta", variant: "secondary" as const, className: "bg-blue-500 text-white hover:bg-blue-600" },
                            fechada: { label: "Fechada", variant: "destructive" as const, className: "bg-orange-500 hover:bg-orange-600" },
                            parcial: { label: "Parcial", variant: "outline" as const, className: "text-muted-foreground" },
                          };
                          const config = statusConfig[status];

                          return (
                            <SelectItem key={invoice.id} value={invoice.id}>
                              <div className="flex items-center justify-between w-full gap-2">
                                <span>{getMonthName(invoice.mes_referencia)} {invoice.ano_referencia}</span>
                                <Badge variant={config.variant} className={config.className}>
                                  {config.label}
                                </Badge>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedInvoice && (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        {getMonthName(selectedInvoice.mes_referencia)} {selectedInvoice.ano_referencia}
                      </CardTitle>
                      {(() => {
                        const status = getInvoiceStatus(selectedInvoice, card);
                        const statusConfig = {
                          paga: { label: "Fatura Paga", variant: "default" as const, className: "bg-green-500" },
                          aberta: { label: "Fatura Aberta", variant: "secondary" as const, className: "bg-blue-500 text-white" },
                          fechada: { label: "Fatura Fechada", variant: "destructive" as const, className: "bg-orange-500" },
                          parcial: { label: "Fatura Parcial", variant: "outline" as const, className: "" },
                        };
                        const config = statusConfig[status];
                        return (
                          <Badge variant={config.variant} className={`px-3 py-1 text-xs font-semibold ${config.className}`}>
                            {config.label}
                          </Badge>
                        );
                      })()}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Valor Total</p>
                        <p className="text-base font-bold">
                          {formatCurrency(selectedInvoice.valor_total)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Fechamento</p>
                        <p className="text-base font-medium">
                          {card.dia_fechamento
                            ? formatDate(
                                formatarDataSQL(
                                  selectedInvoice.ano_referencia,
                                  selectedInvoice.mes_referencia,
                                  card.dia_fechamento
                                )
                              )
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Vencimento</p>
                        <p className="text-base font-medium">
                          {selectedInvoice.data_vencimento
                            ? formatDate(selectedInvoice.data_vencimento)
                            : card.dia_vencimento
                              ? formatDate(
                                  formatarDataSQL(
                                    selectedInvoice.ano_referencia,
                                    selectedInvoice.mes_referencia,
                                    card.dia_vencimento
                                  )
                                )
                              : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {!selectedInvoice.paga && selectedInvoice.valor_total > 0 && (
                      <div className="space-y-3 pt-4 border-t">
                        <Label>Pagar com:</Label>
                        <Select value={selectedPaymentAccount} onValueChange={setSelectedPaymentAccount}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma conta" />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentAccounts.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: account.cor }}
                                  />
                                  {account.nome} - {formatCurrency(account.saldo_atual || 0)}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <div className="space-y-2">
                          <Label>Data do Pagamento:</Label>
                          <DatePicker
                            value={paymentDate}
                            onChange={setPaymentDate}
                            placeholder="Selecione a data do pagamento"
                            className="w-full"
                          />
                        </div>
                        
                        <Button 
                          onClick={handlePayInvoice} 
                          disabled={!selectedPaymentAccount || !paymentDate || payingInvoice}
                          className="w-full"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {payingInvoice ? "Processando..." : "Marcar como Paga"}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Compras da Fatura</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {transactions.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">
                        Nenhuma compra registrada nesta fatura.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {transactions.map((transaction) => (
                          <div
                            key={transaction.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              {transaction.categoria && (
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: transaction.categoria.cor }}
                                />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">
                                    {transaction.descricao?.replace(/\s*\(\d+\/\d+\)\s*$/, '') || ''}
                                  </p>
                                  {(transaction.numero_parcela || (transaction.compras_parceladas && transaction.compras_parceladas.length > 0)) && (
                                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                                      {transaction.numero_parcela || transaction.compras_parceladas?.[0]?.parcela_atual}/{transaction.total_parcelas || transaction.compras_parceladas?.[0]?.numero_parcelas}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <span>{formatDate(transaction.criado_em || transaction.data)}</span>
                                  {transaction.categoria && (
                                    <span>• {transaction.categoria.nome}</span>
                                  )}
                                  {transaction.compras_parceladas && transaction.compras_parceladas.length > 0 && (
                                    <span>• Total: R$ {transaction.compras_parceladas[0].valor_total?.toFixed(2)}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="font-bold text-destructive">
                                {formatCurrency(transaction.valor)}
                              </p>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingTransaction(transaction);
                                  }}
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="z-[60]">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Excluir transação</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Tem certeza que deseja excluir esta transação?
                                        Esta ação não pode ser desfeita.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter className="flex-col sm:flex-row sm:justify-end gap-2">
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      {((((transaction as unknown as { compras_parceladas?: unknown[] }).compras_parceladas?.length ?? 0) > 0 || transaction.transacao_pai_id) || transaction.numero_parcela || transaction.total_parcelas) ? (
                                        <>
                                          <AlertDialogAction
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteTransaction(transaction, false);
                                            }}
                                            className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
                                          >
                                            Apenas esta parcela
                                          </AlertDialogAction>
                                          <AlertDialogAction
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteTransaction(transaction, true);
                                            }}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            Todas as parcelas
                                          </AlertDialogAction>
                                        </>
                                      ) : (
                                        <AlertDialogAction
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteTransaction(transaction, false);
                                          }}
                                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Excluir
                                        </AlertDialogAction>
                                      )}
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <div className="grid gap-4">
              {invoices.map((invoice) => (
                <Card key={invoice.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {getMonthName(invoice.mes_referencia)} {invoice.ano_referencia}
                      </CardTitle>
                      {(() => {
                        const status = getInvoiceStatus(invoice, card);
                        const statusConfig = {
                          paga: { label: "Paga", variant: "default" as const, className: "bg-green-500 hover:bg-green-600" },
                          aberta: { label: "Aberta", variant: "secondary" as const, className: "bg-blue-500 text-white hover:bg-blue-600" },
                          fechada: { label: "Fechada", variant: "destructive" as const, className: "bg-orange-500 hover:bg-orange-600" },
                          parcial: { label: "Parcial", variant: "outline" as const, className: "" },
                        };
                        const config = statusConfig[status];
                        return (
                          <Badge variant={config.variant} className={config.className}>
                            {config.label}
                          </Badge>
                        );
                      })()}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Valor Total</p>
                        <p className="text-base font-bold">
                          {formatCurrency(invoice.valor_total)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Vencimento</p>
                        <p className="font-medium">
                          {formatDate(invoice.data_vencimento)}
                        </p>
                      </div>
                    </div>
                    {invoice.paga && invoice.data_pagamento && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Paga em {formatDate(invoice.data_pagamento)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {editingTransaction && (
        <AdvancedEditTransactionModal
          isOpen={!!editingTransaction}
          onClose={() => setEditingTransaction(null)}
          transaction={editingTransaction as unknown as import("@/types/database").TransactionWithCategory}
          onSuccess={() => {
            if (selectedInvoice) {
              fetchTransactions(selectedInvoice);
            }
          }}
        />
      )}
    </div>
  );
};
